import React from 'react';
import { Box, Skeleton, Card, CardContent, Breadcrumbs, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody, TableContainer } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';

interface AnalysisLoadingSkeletonProps {
  variant?: 'team' | 'league';
}

const skeletonSx = {
  '&::after': {
    animationDuration: '1.6s',
    animationDelay: '0s'
  },
  '&::before': {
    animationDelay: '0s'
  }
};

export const AnalysisLoadingSkeleton: React.FC<AnalysisLoadingSkeletonProps> = ({ 
  variant = 'team' 
}) => {
  if (variant === 'league') {
    return (
      <Box>
        {/* Breadcrumbs skeleton */}
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
          <Skeleton variant="text" width={60} height={20} animation="wave" sx={skeletonSx} />
          <Skeleton variant="text" width={120} height={20} animation="wave" sx={skeletonSx} />
        </Breadcrumbs>

        {/* Header skeleton - matches LeagueAnalysisPage */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'center' }, 
            gap: 2, 
            mb: 1 
          }}>
            <Skeleton variant="circular" width={70} height={70} animation="wave" sx={skeletonSx} />
            <Skeleton variant="text" width="80%" height={48} animation="wave" sx={skeletonSx} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} animation="wave" sx={skeletonSx} />
          </Box>
          <Skeleton variant="text" width="90%" height={24} sx={{ mt: 1, ...skeletonSx }} animation="wave" />
        </Box>

        {/* Tabs skeleton */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={0}>
            <Tab label={<Skeleton variant="text" width={80} animation="wave" sx={skeletonSx} />} />
            <Tab label={<Skeleton variant="text" width={80} animation="wave" sx={skeletonSx} />} />
          </Tabs>
        </Box>

        {/* Table skeleton - matches actual table structure */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2.5fr 1fr' }, gap: 2 }}>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} animation="wave" sx={skeletonSx} /></TableCell>
                    <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                    <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                    <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                    <TableCell><Skeleton variant="text" width={40} animation="wave" sx={skeletonSx} /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" width={20} animation="wave" sx={skeletonSx} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Skeleton variant="circular" width={32} height={32} animation="wave" sx={skeletonSx} />
                          <Skeleton variant="text" width={120} animation="wave" sx={skeletonSx} />
                        </Box>
                      </TableCell>
                      <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                      <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                      <TableCell><Skeleton variant="text" width={30} animation="wave" sx={skeletonSx} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={60} height={20} animation="wave" sx={skeletonSx} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          
          {/* Side panel skeleton */}
          <Card>
            <CardContent>
              <Skeleton variant="text" width={120} height={24} sx={{ mb: 2, ...skeletonSx }} animation="wave" />
              <Skeleton variant="rectangular" width="100%" height={200} animation="wave" sx={skeletonSx} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs skeleton - matches TeamAnalysisPage */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Skeleton variant="text" width={60} animation="wave" sx={skeletonSx} />
        <Skeleton variant="text" width={100} animation="wave" sx={skeletonSx} />
        <Skeleton variant="text" width={120} animation="wave" sx={skeletonSx} />
      </Breadcrumbs>

      {/* Header skeleton - matches simplified structure */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Box sx={{ mr: 2 }}>
            <Skeleton variant="circular" width={48} height={48} animation="wave" sx={skeletonSx} />
          </Box>
          <Box>
            <Skeleton variant="text" width={200} height={40} animation="wave" sx={skeletonSx} />
            <Skeleton variant="text" width="70%" height={24} animation="wave" sx={skeletonSx} />
          </Box>
        </Box>
      </Box>
      
      {/* Current position card skeleton - matches exact structure */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={150} height={28} sx={{ mb: 2, ...skeletonSx }} animation="wave" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="text" width={60} height={72} animation="wave" sx={skeletonSx} />
            <Box>
              <Skeleton variant="text" width={120} height={24} animation="wave" sx={skeletonSx} />
              <Skeleton variant="text" width={100} height={20} animation="wave" sx={skeletonSx} />
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Scenario cards skeleton - matches exact grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2, mb: 3 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <CardContent>
              <Skeleton variant="text" width={100} height={28} sx={{ mb: 1, ...skeletonSx }} animation="wave" />
              <Skeleton variant="text" width={40} height={48} sx={{ mb: 2, ...skeletonSx }} animation="wave" />
              <Skeleton variant="text" width={150} height={20} sx={{ mb: 1, ...skeletonSx }} animation="wave" />
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 1, ...skeletonSx }} animation="wave" />
              <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={skeletonSx} />
            </CardContent>
          </Card>
        ))}
      </Box>
      
      {/* Dependencies card skeleton */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={150} height={28} sx={{ mb: 2, ...skeletonSx }} animation="wave" />
          <Skeleton variant="rectangular" width="100%" height={80} animation="wave" sx={skeletonSx} />
        </CardContent>
      </Card>
      
      {/* Critical matches skeleton */}
      <Card>
        <CardContent>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 2, ...skeletonSx }} animation="wave" />
          {Array.from({ length: 3 }).map((_, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Skeleton variant="text" width={150} height={20} sx={{ mb: 1, ...skeletonSx }} animation="wave" />
              <Skeleton variant="text" width={200} height={16} sx={{ mb: 1, ...skeletonSx }} animation="wave" />
              <Skeleton variant="text" width="80%" height={16} animation="wave" sx={skeletonSx} />
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};
